/* eslint-disable max-lines */
"use client";

import React, { useEffect, useRef, useState } from "react";

import ScrollReveal, {
  ScrollRevealContent,
} from "@/components/common/ScienceScrollReveal/ScienceScrollReveal";
import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import ScienceSlider, {
  ScienceIndicator,
  ScienceSlideContent,
} from "@/components/common/ScienceSlider/ScienceSlider";
import { MinusSmallIcon } from "@/components/icons/MinusSmallIcon";
import { PlusSmallIcon } from "@/components/icons/PlusSmallIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import type {
  ScienceCta,
  ScienceImage as ScienceImageType,
  ScienceIngredient,
  ScienceIngredientsData,
  ScienceResponsiveImage,
} from "@/data/hair/brand/learn/science/science.types";
import { useNavigation } from "@/hooks/useNavigation";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

const TRANSITION_SPEED = 200;
const TEXT_BOX_HEIGHT = 500;

function ResponsiveImage({
  src,
  alt,
  className,
  loading = "lazy",
}: {
  src?: ScienceResponsiveImage;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  if (!src?.mobile && !src?.desktop) return null;
  const fallback = src.mobile || src.desktop || "";
  return (
    <div className="h-full">
      <Picture
        src={fallback}
        sources={
          src.mobile && src.desktop ? [{ media: "(min-width: 768px)", srcSet: src.desktop }] : []
        }
        alt={alt}
        loading={loading}
        className={className}
      />
    </div>
  );
}

function IngredientSlide({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <div data-active={active} className="relative">
      <div
        data-slide
        className={cn(
          "w-36 h-36 min-[374px]:w-48 min-[374px]:h-48 rounded-full overflow-hidden mx-auto transition-[opacity,transform] duration-200",
          active ? "opacity-100 scale-100" : "opacity-50 scale-[0.8]"
        )}
      >
        {children}
      </div>
    </div>
  );
}

function IngredientBannerCard({ cta, image }: { cta?: ScienceCta; image: ScienceImageType }) {
  const navigate = useNavigation();

  const onClick = () => {
    if (!cta) return;
    segmentEvent("Marketing CTA Clicked", {
      destination: cta.button.link,
      module_location: "mid_fold",
      text: cta.button.label,
      type: "button",
    });
    navigate(cta.button.link);
  };

  return (
    <div className="p-4 xl:p-6 h-full min-h-[626px] flex flex-col gap-4 border border-nova-transparency-white-60 bg-[#1e3231]">
      {cta ? (
        <div className="max-w-[400px] mx-auto">
          <ResponsiveImage
            src={image.src}
            alt={image.alt}
            className="w-full object-cover p-[15%]"
          />
        </div>
      ) : (
        <div className="h-full grid place-content-center bg-[#c8d2d1]">
          <ResponsiveImage src={image.src} alt={image.alt} />
        </div>
      )}
      {cta ? (
        <div className="flex-1 flex flex-col justify-center items-center gap-y-6 text-center">
          <TextElementNova
            element="h3"
            text={cta.headline || ""}
            className="Subtitle-10-Dia-Normal max-w-[18ch] text-nova-base-white"
          />
          <ButtonNova icon="Right" onClick={onClick} label={cta.button.label} />
        </div>
      ) : null}
    </div>
  );
}

function IngredientCard(props: ScienceIngredient) {
  const { headline, subheadline, additionalInfo, image, tags } = props;
  const [isActive, setIsActive] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  /** Only enable overflow scroll after expand finishes — avoids scrollbar flash mid-animation */
  const [canScroll, setCanScroll] = useState(false);
  const textBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!textBoxRef.current) return;
    const checkOverflow = () => {
      if (textBoxRef.current) {
        setHasOverflow(textBoxRef.current.scrollHeight > TEXT_BOX_HEIGHT);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [isActive]);

  useEffect(() => {
    if (!isActive) setCanScroll(false);
  }, [isActive]);

  const toggle = () => {
    if (isActive) {
      segmentEvent("Section Collapsed", { section: headline });
    } else {
      segmentEvent("Section Expanded", { section: headline });
    }
    setIsActive((v) => !v);
  };

  return (
    <div
      data-has-overflow={hasOverflow}
      className={cn(
        // h-full keeps Embla-stretched slides equal height (Gatsby min-height 626)
        "relative h-full min-h-[626px] p-4 flex flex-col gap-y-6 rounded-sm border border-nova-transparency-white-60 min-[1550px]:p-6",
        isActive &&
          hasOverflow &&
          "after:content-[''] after:absolute after:pointer-events-none after:bottom-[120px] after:left-0 after:w-full after:h-12 after:bg-gradient-to-t after:from-white after:to-transparent after:z-[1]"
      )}
    >
      <div>
        <ResponsiveImage
          src={image.src}
          alt={image.alt}
          className="w-full object-cover h-[282px]"
        />
      </div>

      <div
        ref={textBoxRef}
        onTransitionEnd={(e) => {
          if (e.propertyName !== "height" || e.target !== textBoxRef.current) return;
          if (isActive) {
            const overflows =
              !!textBoxRef.current && textBoxRef.current.scrollHeight > TEXT_BOX_HEIGHT;
            setHasOverflow(overflows);
            setCanScroll(overflows);
          }
        }}
        className={cn(
          /*
           * transform creates a stacking context that paints over in-flow footer —
           * keep this at z-0 so Show More isn't covered when tags wrap
           */
          "absolute top-0 left-0 z-0 w-full p-4 bg-nova-base-black overflow-hidden min-[1550px]:p-6 transition-[transform,height] duration-150 ease-out",
          isActive ? "translate-y-0 delay-0" : "translate-y-[301px] h-[210px] delay-100",
          canScroll && "overflow-y-auto"
        )}
        style={isActive ? { height: TEXT_BOX_HEIGHT } : undefined}
      >
        <TextElementNova
          element="h4"
          className="Body-30-Dia-Strong text-nova-base-white"
          text={headline}
        />
        {subheadline && (
          <TextElementNova
            className="Body-20-Dia-Normal mt-4 text-nova-base-white"
            text={subheadline}
          />
        )}
        {additionalInfo && (
          <div
            className={cn(
              "py-4 transition-opacity duration-150",
              isActive ? "opacity-100 delay-150" : "opacity-0"
            )}
          >
            <TextElementNova
              text={additionalInfo.headline || ""}
              className="Body-30-Dia-Strong text-nova-base-white"
            />
            <TextElementNova
              text={additionalInfo.text}
              className="Body-20-Dia-Normal mt-4 text-nova-base-white"
            />
          </div>
        )}
      </div>

      <div className="mt-auto relative z-10 bg-nova-base-black">
        <button
          type="button"
          className="slide-button-toggle"
          aria-label={isActive ? "Show Less" : "Show More"}
          onClick={toggle}
        >
          <span className="CTA-20-Dia-Normal border-b border-nova-base-white text-nova-base-white flex gap-x-1 items-center">
            {isActive ? (
              <>
                <MinusSmallIcon className="text-nova-base-white" /> Show Less
              </>
            ) : (
              <>
                <PlusSmallIcon className="text-nova-base-white" /> Show More
              </>
            )}
          </span>
        </button>

        {tags && (
          <div className="flex gap-4 flex-row pt-4 min-h-14">
            <div className="flex items-baseline">
              <TextElementNova
                text="Featured&nbsp;in"
                className="Body-10-Dia-Normal text-nova-base-white"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {tags.map((tag, i) => (
                <div
                  key={i}
                  className="transition-all duration-500 ease-in-out flex items-center justify-center border rounded max-h-[26px] px-2 py-1 text-master-base-default border-master-base-default/20"
                >
                  <TextElementNova
                    text={tag}
                    className="Caption-10-Dia-Uppercase text-nova-base-white !leading-none"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IngredientsMobile({ ingredients, cta, backgroundSequence }: ScienceIngredientsData) {
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigation();

  const onCtaClick = () => {
    if (!cta?.button) return;
    const isQuiz = cta.button.link?.includes("quiz");
    segmentEvent("Marketing CTA Clicked", {
      module_location: "mid_fold",
      destination: isQuiz ? "/hair/brand/quiz/" : cta.button.link,
      text: cta.button.label,
      type: "button",
    });
    if (isQuiz) {
      setTimeout(() => {
        window.location.href = cta.button.link;
      }, 250);
    } else {
      navigate(cta.button.link);
    }
  };

  return (
    <>
      <ScrollReveal
        selector="ingredients"
        backgroundSequence={backgroundSequence}
        showContentTreshold={0.7}
        offset={300}
        backgroundColor="#020e0d"
        content={({ intro }) => (
          <ScrollRevealContent
            intro={intro}
            className="relative h-full w-full min-h-[600px] p-0 flex flex-col items-center justify-between text-center text-master-base-default"
          >
            {/* Text overlays — pointer-events-none so the tall track below receives swipes (Gatsby track z-index: 3) */}
            <div className="w-full h-[120px] max-w-[475px] px-6 absolute top-[55px] z-[2] pointer-events-none">
              <ScienceSlideContent
                delay={TRANSITION_SPEED}
                className="relative h-full"
                active={activeSlide}
                contents={ingredients.map((ingredient) => (
                  <div
                    key={ingredient.headline}
                    className="w-full flex flex-col gap-4 items-center text-nova-base-white"
                  >
                    <TextElementNova
                      element="h4"
                      className="Title-20-Super-Normal text-nova-base-white"
                      text={ingredient.headline}
                    />
                    <TextElementNova
                      element="p"
                      className="Body-20-Dia-Normal text-nova-base-white"
                      text={ingredient.subheadline || ""}
                    />
                  </div>
                ))}
              />
            </div>

            {/* Gatsby: .splide__track { height: 600px; z-index: 3 } + perPage: 2, focus: center, gap: 32 */}
            <div className="relative w-full h-[600px] z-[3]">
              <ScienceSlider
                loop
                align="center"
                showArrows
                className="h-full"
                viewportClassName="h-full"
                containerClassName="h-full items-center"
                slideClassName="flex-[0_0_50%] px-4"
                onMove={(index) => {
                  setActiveSlide(index);
                  segmentEvent("Carousel Clicked", {
                    module_location: "mid_fold",
                    module_name: "Ingredients",
                    slide_number: index + 1,
                    slide_name: ingredients[index]?.headline,
                  });
                }}
              >
                {ingredients.map((slide, index) => (
                  <IngredientSlide key={index} active={activeSlide === index}>
                    <ResponsiveImage
                      src={slide.image.src}
                      alt={slide.image.alt}
                      className="w-full h-full object-cover"
                    />
                  </IngredientSlide>
                ))}
              </ScienceSlider>
            </div>

            <div className="w-full h-[220px] px-6 absolute bottom-[-45px] min-[374px]:bottom-[-65px] max-w-[475px] z-[2] pointer-events-none">
              <div className="pb-4">
                <TextElementNova
                  element="p"
                  className="Body-10-Dia-Strong text-nova-base-white mb-2"
                  text={ingredients[0]?.additionalInfo?.headline || ""}
                />
              </div>
              <ScienceSlideContent
                delay={TRANSITION_SPEED}
                className="relative h-full"
                active={activeSlide}
                contents={ingredients.map((ingredient) => (
                  <div key={ingredient.headline} className="flex flex-col items-center">
                    <TextElementNova
                      element="p"
                      className="Body-10-Dia-Normal text-nova-base-white"
                      text={ingredient?.additionalInfo?.text || ""}
                    />
                  </div>
                ))}
              />
            </div>
            <div className="w-full flex items-center justify-center pb-8 bottom-[-60px] absolute z-[2] pointer-events-none">
              <ScienceIndicator
                speed={TRANSITION_SPEED}
                length={ingredients.length}
                active={activeSlide}
              />
            </div>
          </ScrollRevealContent>
        )}
      />
      {cta ? (
        <div className="px-8 pt-10 flex flex-col gap-8">
          <div className="relative flex flex-col items-center justify-center gap-5 rounded-lg text-center bg-transparent">
            {cta.headline && (
              <TextElementNova
                element="h3"
                text={cta.headline}
                className="Title-10-Super-Normal text-nova-base-white"
              />
            )}
            <ButtonNova
              icon="Right"
              className="cta-banner-button"
              label={cta.button.label}
              onClick={onCtaClick}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Mirrors Gatsby Splide breakpoints in ingredients-desktop.js */
function useIngredientsSliderLayout() {
  const [layout, setLayout] = useState({ padding: 60, perPage: 2, arrowSize: 48 });

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      // Gatsby: 768→2/60, 1280→3/80, 1520→4/110, 1920→5/110; arrows 48→56 at 1280
      if (w >= 1920) setLayout({ padding: 110, perPage: 5, arrowSize: 56 });
      else if (w >= 1520) setLayout({ padding: 110, perPage: 4, arrowSize: 56 });
      else if (w >= 1280) setLayout({ padding: 80, perPage: 3, arrowSize: 56 });
      else setLayout({ padding: 60, perPage: 2, arrowSize: 48 });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return layout;
}

function IngredientsDesktop({
  ingredients,
  cta,
  image,
}: {
  ingredients: ScienceIngredient[];
  cta?: ScienceCta;
  image: ScienceImageType;
}) {
  const { padding, perPage, arrowSize } = useIngredientsSliderLayout();

  if (!ingredients?.length) return null;

  // Splide: slide = (track - padding*2 - gap*(perPage-1)) / perPage, gap: 8
  const gap = 8;
  const gapTotal = gap * (perPage - 1);
  const slideBasis = `calc((100% - ${padding * 2}px - ${gapTotal}px) / ${perPage})`;

  return (
    <div className="relative before:content-[''] before:absolute before:top-0 before:left-0 before:h-full before:w-[5%] before:z-[1] before:pointer-events-none before:bg-gradient-to-r before:from-[rgba(4,28,27,1)] before:to-transparent after:content-[''] after:absolute after:top-0 after:right-0 after:h-full after:w-[5%] after:z-[1] after:pointer-events-none after:bg-gradient-to-l after:from-[rgba(4,28,27,1)] after:to-transparent">
      <ScienceSlider
        loop
        showArrows
        /*
         * Peeks come from edgePadding (Splide padding), not overflow:visible — visible
         * was extending past the viewport and causing page horizontal scroll
         */
        overflow="hidden"
        edgePadding={padding}
        arrowSize={arrowSize}
        arrowVariant="brand"
        slideStyle={{ flex: `0 0 ${slideBasis}`, marginRight: gap, height: "100%" }}
        containerClassName="items-stretch"
        viewportClassName="h-full"
        onMove={(index) => {
          segmentEvent("Carousel Clicked", {
            module_location: "mid_fold",
            module_name: "Ingredients",
            slide_number: index,
          });
        }}
      >
        {ingredients.slice(0, 3).map((slide, i) => (
          <IngredientCard key={i} {...slide} />
        ))}
        <IngredientBannerCard image={image} cta={cta} />
        {ingredients.slice(3).map((slide, i) => (
          <IngredientCard key={i + 3} {...slide} />
        ))}
      </ScienceSlider>
    </div>
  );
}

export default function ScienceIngredients(props: ScienceIngredientsData) {
  const { intro, id } = props;

  return (
    <section
      data-section
      id={id}
      className="relative z-[1] overflow-x-hidden py-8 md:py-12 xl:py-20 bg-nova-base-black"
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} background="dark" headingWidth={0.46} />}
      </div>
      <div className="md:hidden">
        <IngredientsMobile {...props} />
      </div>
      <div className="hidden md:block">
        <IngredientsDesktop ingredients={props.ingredients} cta={props.cta} image={props.image} />
      </div>
    </section>
  );
}
